import {
  Button,
  Col,
  Divider,
  Form,
  Modal,
  ModalProps,
  Row,
  Skeleton,
  TimePicker,
  notification,
} from 'antd';
import { UserReduced } from '@/core/domain/enterprise/Users/Users.types';
import React, { useEffect } from 'react';
import moment, { Moment } from 'moment';
import { DeleteIcon, FloatingFormItem } from '@/components/_common';
import {
  UserWorkTimeControllerFormValues,
  weekDayType,
  workTimeList,
} from '@/core/domain/enterprise/UserWorkTimeController/UserWorkTimeController.types';
import {
  useCreateUserWorkTime,
  useRemoveUserWorkTime,
  useUpdateUserWorkTime,
  useUserWorkTime,
} from '@/core/domain/enterprise/UserWorkTimeController/UserWorkTimeController.hooks';

interface Props extends ModalProps {
  user?: UserReduced;
  onCancel: VoidFunction;
}

interface Day {
  label: string;
  rangeTime: [Moment | undefined, Moment | undefined];
  id?: number;
  weekDay: weekDayType;
}

const days: Day[] = [
  {
    label: 'Segunda-feira',
    weekDay: weekDayType.MONDAY,
    rangeTime: [undefined, undefined],
  },
  {
    label: 'Terça-feira',
    weekDay: weekDayType.TUESDAY,
    rangeTime: [undefined, undefined],
  },
  {
    label: 'Quarta-feira',
    weekDay: weekDayType.WEDNESDAY,
    rangeTime: [undefined, undefined],
  },
  {
    label: 'Quinta-feira',
    weekDay: weekDayType.THURSDAY,
    rangeTime: [undefined, undefined],
  },
  {
    label: 'Sexta-feira',
    weekDay: weekDayType.FRIDAY,
    rangeTime: [undefined, undefined],
  },
  {
    label: 'Sábado',
    weekDay: weekDayType.SATURDAY,
    rangeTime: [undefined, undefined],
  },
  {
    label: 'Domingo',
    weekDay: weekDayType.SUNDAY,
    rangeTime: [undefined, undefined],
  },
];

const UserModalAccessClock: React.FC<Props> = ({ user, ...props }) => {
  const [form] = Form.useForm<UserWorkTimeControllerFormValues>();
  const fill = Form.useWatch('fill', form);
  const times: Day[] = Form.useWatch('times', form);

  const { mutateAsync: create, isLoading: createLoading } =
    useCreateUserWorkTime();
  const { mutateAsync: update, isLoading: updateLoading } =
    useUpdateUserWorkTime();
  const { mutateAsync: remove, isLoading: removeLoading } =
    useRemoveUserWorkTime();
  const { data, dataUpdatedAt, isLoading } = useUserWorkTime(
    props.open ? user?.id : undefined,
  );

  const handleFill = async () => {
    days.forEach(() => {
      form.setFieldValue(
        'times',
        days.map((day) => {
          const item = data?.find((item) => item.weekDay === day.weekDay);

          return {
            ...day,
            rangeTime: [
              fill ? moment(fill[0], 'HH:mm:ss') : null,
              fill ? moment(fill[1], 'HH:mm:ss') : null,
            ],
            id: item?.id,
            weekDay: item?.weekDay,
          };
        }),
      );
    });
  };

  const handleSubimit = async () => {
    const workTimeList: workTimeList[] = [];

    if (user?.id) {
      times?.forEach((time, index) =>
        workTimeList.push({
          id: time.id,
          weekDay: days[index].weekDay,
          userId: user.id,
          startTime: time.rangeTime?.[0]
            ? moment(time.rangeTime[0]).format('HH:mm:ss')
            : undefined,
          endTime: time.rangeTime?.[1]
            ? moment(time.rangeTime[1]).format('HH:mm:ss')
            : undefined,
        }),
      );
    }

    const filterTimeCreated = workTimeList.filter(({ id }) => id);
    const filterTimeNotCreated = workTimeList.filter(
      ({ id, startTime }) => !id && startTime?.length,
    );

    if (filterTimeNotCreated.length) {
      await create({ workTimeList: filterTimeNotCreated });
    }

    if (filterTimeCreated.length) {
      await update({ workTimeList: filterTimeCreated });
    }
    notification.success({ message: 'Horário atualizado com sucesso!' });
    props.onCancel();
  };

  useEffect(() => {
    if (data?.length) {
      form.setFieldValue(
        'times',
        days.map((day) => {
          const item = data.find((item) => item.weekDay === day.weekDay);

          return {
            ...day,
            rangeTime: [
              item?.startTime ? moment(item?.startTime, 'HH:mm:ss') : null,
              item?.endTime ? moment(item?.endTime, 'HH:mm:ss') : null,
            ],
            id: item?.id,
            weekDay: item?.weekDay,
          };
        }),
      );
    }
  }, [dataUpdatedAt]);

  const handleClose = () => {
    form.resetFields();
    !!props.onCancel && props.onCancel();
  };

  const handleRemove = async (index: number) => {
    await remove(times[index].id);
    form.resetFields(['times', index, 'rangeTime']);
  };

  const calculateHourDifference = (
    rangeTime?: [Moment | undefined, Moment | undefined],
  ) => {
    if (!rangeTime?.[0] || !rangeTime?.[1]) return '24h';

    const startMoment = rangeTime[0];
    const endMoment = rangeTime[1];

    const duration = moment.duration(endMoment.diff(startMoment));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    return `${hours}h${
      minutes > 0 ? `${minutes < 10 ? '0' : ''}${minutes}` : ''
    }`;
  };

  return (
    <Modal
      width={500}
      title="Horário permitido de acesso"
      okText="Salvar"
      cancelText="Fechar"
      onOk={handleSubimit}
      {...props}
      onCancel={handleClose}
      okButtonProps={{
        loading: createLoading || updateLoading || removeLoading,
      }}
    >
      <Form form={form}>
        <Row gutter={[12, 12]} align="middle">
          <Col span={24} md={16}>
            <FloatingFormItem
              label="Preenchimento automático"
              name="fill"
              initialValue={[
                moment('08:00', 'HH:mm:ss'),
                moment('18:00', 'HH:mm:ss'),
              ]}
              style={{ marginBottom: 0 }}
            >
              <TimePicker.RangePicker
                style={{ width: '100%' }}
                format="HH:mm"
                allowClear
                suffixIcon={
                  <span style={{ color: 'green' }}>
                    {calculateHourDifference([fill?.[0], fill?.[1]])}
                  </span>
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8}>
            <Button block type="primary" onClick={handleFill}>
              Preencher
            </Button>
          </Col>
        </Row>

        <Divider />

        <Skeleton loading={isLoading} active paragraph={{ rows: 10 }}>
          <Form.List name="times" initialValue={days}>
            {(fields) => {
              return fields.map((field, index) => (
                <div key={index}>
                  <Form.Item hidden noStyle name={[field.key, 'id']} />
                  <Form.Item hidden noStyle name={[field.key, 'weekDay']} />

                  <Row gutter={12} align="middle">
                    <Col flex="1">
                      <FloatingFormItem
                        label={days[index].label}
                        name={[field.key, 'rangeTime']}
                        parentFieldName="times"
                      >
                        <TimePicker.RangePicker
                          style={{ width: '100%' }}
                          format="HH:mm"
                          allowClear={
                            !data?.some(({ id }) => id === times?.at(index)?.id)
                          }
                          suffixIcon={
                            <>
                              <p style={{ color: 'green' }}>Total de horas:</p>

                              <span style={{ color: 'green' }}>
                                {calculateHourDifference(
                                  times?.[index]?.rangeTime,
                                )}
                              </span>
                            </>
                          }
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col>
                      <DeleteIcon
                        disabled={
                          !data?.some(
                            ({ id }) => id === times?.at(index)?.id,
                          ) || removeLoading
                        }
                        onClick={() => handleRemove(index)}
                      />
                    </Col>
                  </Row>
                </div>
              ));
            }}
          </Form.List>
        </Skeleton>
      </Form>
    </Modal>
  );
};

export default UserModalAccessClock;
